import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaChartLine, FaServer, FaCodeBranch } from 'react-icons/fa';

const StakingFundPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Blockchain Fund', path: '/fund/blockchain' },
    { label: 'Staking Fund' }
  ];

  const stakingAssets = [
    {
      name: "Ethereum (ETH)",
      info: "Proof-of-Stake validation on the Ethereum network",
      yield: "~5.2% APY"
    },
    {
      name: "Solana (SOL)",
      info: "Validator node operation for the Solana blockchain",
      yield: "~7.1% APY"
    },
    {
      name: "Polkadot (DOT)",
      info: "Nomination staking on the Polkadot network",
      yield: "~14% APY"
    },
    {
      name: "Cardano (ADA)",
      info: "Secure staking pool operations for Cardano",
      yield: "~5.8% APY"
    },
    {
      name: "Avalanche (AVAX)",
      info: "Node validation for the Avalanche network",
      yield: "~9.2% APY"
    },
    {
      name: "Cosmos (ATOM)",
      info: "Validator operations in the Cosmos ecosystem",
      yield: "~12% APY"
    }
  ];

  const features = [
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Institutional-Grade Security",
      description: "Multiple layers of security protocols, cold storage solutions, and 24/7 monitoring to ensure asset safety."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Optimized Yield",
      description: "Advanced staking strategies and validator management to maximize rewards while maintaining operational stability."
    },
    {
      icon: <FaServer className="w-8 h-8 text-secondary" />,
      title: "Enterprise Infrastructure",
      description: "Redundant, geographically distributed nodes with 99.9% uptime guarantee and automated failover systems."
    },
    {
      icon: <FaCodeBranch className="w-8 h-8 text-secondary" />,
      title: "Multi-Chain Expertise",
      description: "Specialized teams for each blockchain protocol ensuring optimal performance across diverse staking mechanisms."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Staking Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Secure Passive Income Through Blockchain Staking
              </h2>
              <p className="text-lg text-gray-700">
                Our Staking Fund enables investors to participate in blockchain network validation and earn regular staking rewards from multiple leading Proof-of-Stake networks, all managed by our expert technical team.
              </p>
              <p className="text-lg text-gray-700">
                By pooling resources, we operate institutional-grade validator nodes with maximum efficiency, security and uptime, providing consistent returns without the technical complexity of self-managed staking operations.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Explore Staking Opportunities
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/visualization-energy-flowing-through-power-tower-lines-night-sky-infrastructure-ensuring-transmission-electricity-through-voltage-distribution-cables-3d-render-animation_482257-66243.jpg_pa73rd.jpg" 
                alt="Staking Network Visualization" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Featured Staking Assets
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our fund maintains active staking positions across multiple top-tier proof-of-stake blockchains, providing diversified exposure to staking rewards.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {stakingAssets.map((asset, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-xl font-heading font-semibold mb-2">{asset.name}</h4>
                    <p className="text-gray-600 text-sm">{asset.info}</p>
                  </div>
                  <div className="bg-secondary/10 text-secondary font-semibold rounded-full px-3 py-1">
                    {asset.yield}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-8">
              Our Staking Advantage
            </h3>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">$65M+</div>
                <div className="text-gray-300">Assets Staked</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">8.5%</div>
                <div className="text-gray-300">Average Annual Yield</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">99.98%</div>
                <div className="text-gray-300">Validator Uptime</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default StakingFundPage;
